
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RotateCw, Loader2 } from 'lucide-react';

interface BulletinsInfiniteLoaderProps {
  onLoadMore: () => void;
  isLoading: boolean;
  hasNextPage?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

const BulletinsInfiniteLoader = ({ 
  onLoadMore, 
  isLoading, 
  hasNextPage = false,
  hasError = false,
  onRetry
}: BulletinsInfiniteLoaderProps) => {
  const observerTarget = useRef<HTMLDivElement>(null);
  const [errorShown, setErrorShown] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  
  // Debounce intersections to prevent multiple calls
  const intersectionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const observerCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    
    if (entry.isIntersecting) {
      setIsIntersecting(true);
      
      // Clear any existing timeout
      if (intersectionDebounceRef.current) {
        clearTimeout(intersectionDebounceRef.current);
      }
      
      // Debounce the intersection callback
      intersectionDebounceRef.current = setTimeout(() => {
        if (!isLoading && !hasError && hasNextPage) {
          try {
            onLoadMore();
          } catch (error) {
            console.error('Error in infinite loader intersection callback:', error);
          }
        }
      }, 200); // 200ms debounce
    } else {
      setIsIntersecting(false);
    }
  }, [onLoadMore, isLoading, hasError, hasNextPage]);

  // Reset error state when props change
  useEffect(() => {
    if (!hasError) {
      setErrorShown(false);
    } else if (hasError && !errorShown) {
      setErrorShown(true);
    }
  }, [hasError, errorShown]);

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '300px', // Load earlier for smoother experience
      threshold: 0.1
    });

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      observer.disconnect();
      // Clear any pending timeouts
      if (intersectionDebounceRef.current) {
        clearTimeout(intersectionDebounceRef.current);
      }
    };
  }, [observerCallback]);

  const handleRetry = () => {
    setErrorShown(false);
    if (onRetry) {
      try {
        onRetry();
      } catch (error) {
        console.error('Error in retry handler:', error);
      }
    } else {
      try {
        onLoadMore();
      } catch (error) {
        console.error('Error in fallback intersection handler:', error);
      }
    }
  };

  return (
    <div 
      ref={observerTarget} 
      className="w-full h-20 flex items-center justify-center"
      data-intersecting={isIntersecting}
    >
      {/* Loading state is now hidden as requested */}

      {hasError && errorShown && (
        <div className="flex flex-col items-center space-y-2">
          <p className="text-red-400">Error loading more bulletins</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRetry}
            className="flex items-center space-x-1"
          >
            <RotateCw className="h-4 w-4 mr-2" />
            Try again
          </Button>
        </div>
      )}
    </div>
  );
};

export default BulletinsInfiniteLoader;
